import { useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, brl, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
const str = (v) => (v ? String(v) : "");

function estimate(f, km) {
  if (num(f.max_km) && km > num(f.max_km)) return null;
  let price = km * num(f.price_per_km);
  if (f.price_mode === "raios") {
    const band = f.radii.filter((r) => num(r.km) > 0).sort((a, b) => num(a.km) - num(b.km)).find((r) => km <= num(r.km));
    if (!band) return null;
    price = num(band.fee);
  }
  return Math.max(price, num(f.min_price));
}

function Radii({ radii, onChange }) {
  const patch = (i, k, v) => onChange(radii.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid="boy-radii">
      {radii.map((r, i) => (
        <div key={i} className="flex items-end gap-2">
          <Field label="Até (km)"><Input type="number" min="0" step="0.5" value={r.km} onChange={(e) => patch(i, "km", e.target.value)} className="w-28" data-testid={`boy-radius-km-${i}`} /></Field>
          <Field label="Valor (R$)"><Input type="number" min="0" step="0.5" value={r.fee} onChange={(e) => patch(i, "fee", e.target.value)} className="w-28" data-testid={`boy-radius-fee-${i}`} /></Field>
          <Button size="icon" variant="ghost" onClick={() => onChange(radii.filter((_, j) => j !== i))} data-testid={`boy-radius-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...radii, { km: "", fee: "" }])} data-testid="boy-add-radius-btn"><Plus className="mr-1 h-4 w-4" />Adicionar faixa</Button>
    </div>
  );
}

export function PricingCard({ config, onSaved }) {
  const [f, setF] = useState(() => ({ ...config, price_per_km: str(config.price_per_km), min_price: str(config.min_price), commission_pct: str(config.commission_pct),
    max_km: str(config.max_km), radii: config.radii.map((r) => ({ km: String(r.km), fee: String(r.fee) })) }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { price_mode: f.price_mode, price_per_km: num(f.price_per_km), min_price: num(f.min_price), commission_pct: num(f.commission_pct), max_km: num(f.max_km),
        radii: f.radii.filter((r) => num(r.km) > 0).map((r) => ({ km: num(r.km), fee: num(r.fee) })) };
      const { data } = await api.put("/boy/settings", body);
      onSaved(data.config);
      toast.success("Valores do Boy Express salvos");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const mode = (k, label) => (
    <button type="button" onClick={() => setF((x) => ({ ...x, price_mode: k }))} data-testid={`boy-price-mode-${k}`}
      className={cn("rounded-md px-3 py-2 text-sm font-medium transition-colors", f.price_mode === k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>{label}</button>
  );
  const ex = [3, 8].map((km) => [km, estimate(f, km)]);
  const pct = num(f.commission_pct);
  return (
    <section className="card-surface space-y-5 p-5" data-testid="boy-pricing-card">
      <p className="eyebrow">Valores das corridas</p>
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">{mode("km", "Valor por km")}{mode("raios", "Faixas de distância")}</div>
      {f.price_mode === "km"
        ? <Field label="Valor por km (R$)" hint="Multiplicado pela distância pelas ruas"><Input type="number" min="0" step="0.1" value={f.price_per_km} onChange={set("price_per_km")} data-testid="boy-price-per-km-input" /></Field>
        : <div className="space-y-2"><p className="text-sm font-medium">Faixas de distância</p><p className="text-xs text-muted-foreground">Como no delivery: a corrida usa o valor da primeira faixa que cobre a distância.</p><Radii radii={f.radii} onChange={(radii) => setF((x) => ({ ...x, radii }))} /></div>}
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Valor mínimo (R$)"><Input type="number" min="0" step="0.5" value={f.min_price} onChange={set("min_price")} data-testid="boy-min-price-input" /></Field>
        <Field label="Comissão da empresa (%)" hint="Fica com a empresa; o resto é do motoboy"><Input type="number" min="0" max="100" step="0.5" value={f.commission_pct} onChange={set("commission_pct")} data-testid="boy-commission-input" /></Field>
        <Field label="Distância máxima (km)" hint="0 = sem limite"><Input type="number" min="0" step="0.5" value={f.max_km} onChange={set("max_km")} data-testid="boy-max-km-input" /></Field>
      </div>
      <div className="space-y-1 rounded-lg bg-muted/50 p-3 text-sm" data-testid="boy-price-preview">
        {ex.map(([km, v]) => <p key={km}>Corrida de {km} km: {v == null ? <span className="text-muted-foreground">fora da área atendida</span> : <><b>{brl(v)}</b><span className="text-muted-foreground"> · empresa {brl(v * pct / 100)} · motoboy {brl(v - v * pct / 100)}</span></>}</p>)}
      </div>
      <Button onClick={save} disabled={busy} className="h-11 w-full" data-testid="boy-pricing-save-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salvar valores</Button>
    </section>
  );
}
